/**
 * Thin HTTP layer for the Zunox Hub's admin API. The browser never talks to
 * the Hub directly — `aw_server/hub_proxy.py` forwards `/v1/admin/*`
 * requests to it server-side, so every call here is a same-origin relative
 * path and the session cookie is sent automatically.
 */
export class HubApiError extends Error {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "status", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
    }
}
/** Sends a request and returns the response once it's known to be OK. */
async function send(path, options = {}) {
    let response;
    try {
        response = await fetch(path, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...(options.headers || {}),
            },
        });
    }
    catch {
        throw new HubApiError('Could not reach the Hub. Is zunox-hub-server running?');
    }
    if (!response.ok) {
        const error = new HubApiError(`Hub request to ${path} failed (HTTP ${response.status})`);
        error.status = response.status;
        throw error;
    }
    return response;
}
export async function request(path, options = {}) {
    const response = await send(path, options);
    if (response.status === 204) {
        return undefined;
    }
    return (await response.json());
}
export async function getSession() {
    return request('/v1/admin/session');
}
export const post = (body) => ({
    method: 'POST',
    body: JSON.stringify(body),
});
/** How to stretch this email's password before signing in. */
export async function prelogin(email) {
    return (await request('/v1/admin/auth/prelogin', post({ email }))).kdf;
}
export function needsSecondFactor(reply) {
    return 'second_factor' in reply;
}
/** Signs in with the auth key derived from the password. */
export async function login(email, authKey) {
    await afterLogout();
    return request('/v1/admin/auth/login', post({ email, auth_key: authKey }));
}
/** The sign-out still on its way to the Hub, if any. Every request that
 * starts a session waits for it first: the Hub's answer to a sign-out
 * clears the session cookie, and if it arrived after the next person's
 * sign-in it would clear theirs. */
let loggingOut = Promise.resolve();
export function afterLogout() {
    return loggingOut;
}
export async function logout() {
    const run = request('/v1/admin/auth/logout', { method: 'POST' });
    loggingOut = run.catch(() => undefined);
    await run;
}
export async function setupStatus() {
    return request('/v1/admin/auth/setup');
}
/** Whether the Hub still needs its first admin. */
export async function setupNeeded() {
    return (await setupStatus()).needed;
}
/** Asks for a reset link by email. The reply is the same whether or not
 * the account exists. */
export async function forgotPassword(email) {
    await request('/v1/admin/auth/forgot-password', post({ email }));
}
export async function setUpFirstAdmin(token, email, displayName, credentials) {
    await afterLogout();
    return request('/v1/admin/auth/setup', post({ token, email, display_name: displayName, credentials }));
}
export async function peekInvite(token) {
    return request(`/v1/admin/auth/invite/${encodeURIComponent(token)}`);
}
export async function acceptInvite(token, credentials, secondFactorCode = null) {
    await afterLogout();
    return request('/v1/admin/auth/invite', post(secondFactorCode
        ? { token, credentials, second_factor_code: secondFactorCode }
        : { token, credentials }));
}
/** Replaces the password; the key pair stays the same. */
export async function changePassword(currentAuthKey, credentials) {
    await afterLogout();
    return request('/v1/admin/account/password', post({ current_auth_key: currentAuthKey, credentials }));
}
export async function listPeople() {
    return request('/v1/admin/people');
}
export async function invitePerson(email, displayName, isFirmAdmin, isEngine = false) {
    return request('/v1/admin/people', post({
        email,
        display_name: displayName,
        is_firm_admin: isFirmAdmin,
        is_engine: isEngine,
    }));
}
export async function updatePerson(userId, update) {
    await request(`/v1/admin/people/${userId}`, post(update));
}
/** A new invite link for someone who hasn't set a password yet. */
export async function reissueInvite(userId) {
    return request(`/v1/admin/people/${userId}/invite`, { method: 'POST' });
}
/** Clears a forgotten password and returns a link to choose a new one. */
export async function resetPerson(userId) {
    return request(`/v1/admin/people/${userId}/reset`, { method: 'POST' });
}
export async function createOrganization(slug, name, isDemo = false) {
    return request('/v1/admin/organizations', post(isDemo ? { slug, name, is_demo: true } : { slug, name }));
}
/** Renames a client; devices are tied to its id, so they're unaffected. */
export async function renameOrganization(organizationId, slug, name) {
    await request(`/v1/admin/organizations/${organizationId}`, post({ slug, name }));
}
/** Archives a client (or restores it): nothing is deleted either way. */
export async function setOrganizationArchived(organizationId, archived) {
    const action = archived ? 'archive' : 'restore';
    await request(`/v1/admin/organizations/${organizationId}/${action}`, post({}));
}
export async function listMyKeyGrants() {
    return request('/v1/admin/key-grants');
}
/** Drops the signed-in user's own copy of a client's key (it was found
 * unusable), so a good one can be shared in its place. */
/** Drops this user's own copy of a client's key, only if it is still
 * exactly `sealedSecret` (the copy examined): a fresh copy shared since is
 * kept (the Hub answers 409). */
export async function deleteMyKeyGrant(organizationId, sealedSecret) {
    await request(`/v1/admin/key-grants/${organizationId}`, {
        method: 'DELETE',
        body: JSON.stringify({ sealed_secret: sealedSecret }),
    });
}
export async function listSessions() {
    return request('/v1/admin/account/sessions');
}
export async function endSession(sessionId) {
    await request(`/v1/admin/account/sessions/${sessionId}`, { method: 'DELETE' });
}
export async function signOutOtherSessions() {
    const body = await request('/v1/admin/account/sessions/sign-out-others', {
        method: 'POST',
    });
    return body.signed_out;
}
/** The signed-in user's encrypted pins, or null when none are saved. */
export async function getPins() {
    const body = await request('/v1/admin/account/pins');
    return body?.blob ?? null;
}
/** Turns the signed-in user's daily devices-not-reporting email on or off. */
export async function setDeviceAlerts(enabled) {
    await request('/v1/admin/account/device-alerts', {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
    });
}
export async function putPins(blob) {
    await request('/v1/admin/account/pins', { method: 'PUT', body: JSON.stringify({ blob }) });
}
export async function listKeyRecipients(organizationId) {
    return request(`/v1/admin/organizations/${organizationId}/key-recipients`);
}
export async function grantKey(organizationId, userId, recipientKey, orgKeyVersion, sealedSecret) {
    await request(`/v1/admin/organizations/${organizationId}/key-grants`, post({
        user_id: userId,
        recipient_key: recipientKey,
        org_key_version: orgKeyVersion,
        sealed_secret: sealedSecret,
    }));
}
/** The firm's activity log, newest first; firm admins only. */
export async function listAuditEvents(organizationId, before, limit) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (organizationId)
        params.set('organization_id', organizationId);
    if (before !== null)
        params.set('before', String(before));
    return request(`/v1/admin/audit?${params}`);
}
export async function addMember(organizationId, userId) {
    await request(`/v1/admin/organizations/${organizationId}/members`, post({ user_id: userId }));
}
export async function removeMember(organizationId, userId) {
    await request(`/v1/admin/organizations/${organizationId}/members/${userId}`, {
        method: 'DELETE',
    });
}
/** Deletes an archived client and everything of its own (devices, records,
 * keys, check sheets). Only once archived; the Hub refuses otherwise. */
export async function deleteArchivedOrganization(organizationId) {
    await request(`/v1/admin/organizations/${organizationId}`, { method: 'DELETE' });
}
export async function listOrganizations() {
    return request('/v1/admin/organizations');
}
/** `null` means the organization hasn't published a key yet. */
export async function getOrgKey(organizationId) {
    try {
        return await request(`/v1/admin/organizations/${organizationId}/org-key`);
    }
    catch (err) {
        if (err instanceof HubApiError && err.status === 404)
            return null;
        throw err;
    }
}
/** Rotates to a new key: `signature` is the identity's activation of it
 * (see orgSigning.activationMessage), as the next version and sequence. */
export async function publishOrgKey(organizationId, publicKeyBase64, signature) {
    return request(`/v1/admin/organizations/${organizationId}/org-key`, {
        method: 'POST',
        body: JSON.stringify({ public_key: publicKeyBase64, signature }),
    });
}
/** Sets up the organization's signing identity, once, with its first
 * activation: of the key in use, or of the organization's first key. */
export async function setUpOrgIdentity(organizationId, identityPublicKey, publicKeyBase64, signature) {
    return request(`/v1/admin/organizations/${organizationId}/org-key/identity`, {
        method: 'POST',
        body: JSON.stringify({
            identity_public_key: identityPublicKey,
            public_key: publicKeyBase64,
            signature,
        }),
    });
}
export async function listOrgKeyHistory(organizationId) {
    return request(`/v1/admin/organizations/${organizationId}/org-key/history`);
}
/** Re-activates a retired key version, signed by the identity like any
 * key change. */
export async function rollbackOrgKey(organizationId, keyVersion, signature) {
    return request(`/v1/admin/organizations/${organizationId}/org-key/rollback`, {
        method: 'POST',
        body: JSON.stringify({ key_version: keyVersion, signature }),
    });
}
/** Stored record counts (summaries and sealed days) per key version,
 * across every device. */
export async function listKeyVersionUsage(organizationId) {
    return request(`/v1/admin/organizations/${organizationId}/org-key/versions`);
}
/** One page of summaries still under `keyVersion`, ordered by id. */
export async function listRecordsForKeyVersion(organizationId, keyVersion, after, limit = 100) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (after)
        params.set('after', after);
    return request(`/v1/admin/organizations/${organizationId}/org-key/versions/${keyVersion}/records?${params}`);
}
/** Swaps stored summaries under `fromKeyVersion` for envelopes re-encrypted
 * to the active key. Returns how many rows the Hub actually replaced. */
export async function reencryptRecords(organizationId, fromKeyVersion, records) {
    return request(`/v1/admin/organizations/${organizationId}/org-key/versions/${fromKeyVersion}/reencrypt`, { method: 'POST', body: JSON.stringify({ records }) });
}
/** One page of sealed days still under `keyVersion`. Each record's `id` is
 * `<device id>:<day>`; pass the last one as `after` for the next page. */
export async function listDayBundlesForKeyVersion(organizationId, keyVersion, after, limit = 100) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (after)
        params.set('after', after);
    return request(`/v1/admin/organizations/${organizationId}/org-key/versions/${keyVersion}/day-bundles?${params}`);
}
/** Swaps sealed days under `fromKeyVersion` for envelopes re-encrypted to
 * the active key. A day its agent re-sealed meanwhile keeps the agent's. */
export async function reencryptDayBundles(organizationId, fromKeyVersion, records) {
    return request(`/v1/admin/organizations/${organizationId}/org-key/versions/${fromKeyVersion}/day-bundles/reencrypt`, { method: 'POST', body: JSON.stringify({ records }) });
}
/** One page of the client's engine items still under `keyVersion`, with what
 * each is listed as. */
export async function listEngineItemsForKeyVersion(organizationId, keyVersion, after, limit = 100) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (after)
        params.set('after', after);
    return request(`/v1/admin/organizations/${organizationId}/org-key/versions/${keyVersion}/engine-items?${params}`);
}
/** Replaces engine items under `fromKeyVersion` with bodies moved onto the
 * client's active key. */
export async function reencryptEngineItems(organizationId, fromKeyVersion, records) {
    return request(`/v1/admin/organizations/${organizationId}/org-key/versions/${fromKeyVersion}/engine-items/reencrypt`, { method: 'POST', body: JSON.stringify({ records }) });
}
/** Approves a device that enrolled itself with the organization ID. Until
 * then the Hub refuses its activity; it keeps capturing locally and uploads
 * once approved. Once the organization has a signing identity, approval
 * carries the identity's endorsement of the device's signing key. */
export async function approveDevice(organizationId, deviceId, endorsement = null) {
    await request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/approve`, {
        method: 'POST',
        ...(endorsement ? { body: JSON.stringify({ endorsement }) } : {}),
    });
}
/** Endorses an approved device's signing key (one approved before
 * endorsements existed, or that registered its key afterwards). */
export async function endorseDevice(organizationId, deviceId, endorsement) {
    await request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/endorse`, {
        method: 'POST',
        body: JSON.stringify({ endorsement }),
    });
}
export async function listDevices(organizationId) {
    return request(`/v1/admin/organizations/${organizationId}/devices`);
}
/** Revokes a device — it stops syncing immediately, but its history (and
 * any summaries already received from it) stays visible; not reversible by
 * re-pairing with its old credential. */
export async function revokeDevice(organizationId, deviceId) {
    await request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}`, {
        method: 'DELETE',
    });
}
/** The tracker version devices should be running. */
export async function getCurrentAgentVersion() {
    const body = await request('/v1/admin/agent-version');
    return body.current_version;
}
/** The organization's default tracking schedule and time zone. */
export async function getCaptureDefaults(organizationId) {
    return request(`/v1/admin/organizations/${organizationId}/capture-defaults`);
}
export async function setCaptureDefaults(organizationId, defaults) {
    await request(`/v1/admin/organizations/${organizationId}/capture-defaults`, {
        method: 'POST',
        body: JSON.stringify(defaults),
    });
}
/** A device's pause, end date and overrides. Its agent applies them within
 * about 20 seconds while it's online, and on its next check-in otherwise. */
export async function setDeviceCaptureControls(organizationId, deviceId, controls) {
    await request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/capture-controls`, { method: 'POST', body: JSON.stringify(controls) });
}
/** Permanently deletes an already-revoked device and every summary it ever
 * sent. Only works on a device that's already revoked — the Hub refuses to
 * purge one still active. */
export async function purgeDevice(organizationId, deviceId) {
    await request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/purge`, {
        method: 'DELETE',
    });
}
export async function listDeviceSummaries(organizationId, deviceId, limit = 50) {
    return request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/summaries?limit=${limit}`);
}
/** A device's summaries received in `[since, until)`, newest first, one page
 * at a time: pass the last id of a page as `after` for the next. */
export async function listDeviceSummariesInRange(organizationId, deviceId, since, until, after = null, limit = 500) {
    const params = new URLSearchParams({ since, until, limit: String(limit) });
    if (after)
        params.set('after', after);
    return request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/summaries?${params}`);
}
/**
 * Stores a day this browser sealed. `stored`, or `refused` (409) when the
 * agent already sealed that day, whose copy always wins, or when the key
 * rotated since the envelope was made. Either way there is nothing to retry.
 */
export async function sealDeviceDay(organizationId, deviceId, bundle) {
    try {
        await request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/day-bundles`, { method: 'POST', body: JSON.stringify(bundle) });
        return 'stored';
    }
    catch (err) {
        if (err instanceof HubApiError && err.status === 409)
            return 'refused';
        throw err;
    }
}
/** Most pages one listing follows, as a backstop against a Hub that keeps
 * naming a next day. At the Hub's page size this is gigabytes. */
const MAX_DAY_BUNDLE_PAGES = 100;
/** A device's sealed days from `from` to `to` inclusive (`YYYY-MM-DD`, UTC,
 * at most 400 days apart), oldest first. Days not sealed yet are absent. The
 * Hub returns a bounded amount per response and names the next day to ask
 * for in `Zunox-Next-Day`; this follows it to the end of the range. */
export async function listDeviceDayBundles(organizationId, deviceId, from, to) {
    const bundles = [];
    let next = from;
    for (let page = 0; next && page < MAX_DAY_BUNDLE_PAGES; page++) {
        const params = new URLSearchParams({ from: next, to });
        const response = await send(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/day-bundles?${params}`);
        bundles.push(...(await response.json()));
        const following = response.headers.get('zunox-next-day');
        // Only ever forward, so a confused Hub can't loop this.
        next = following && following > next && following <= to ? following : null;
    }
    return bundles;
}
/** The device's latest live snapshot, or null if it has never sent one
 * (an older agent, or one that has not started yet). */
export async function getDeviceLive(organizationId, deviceId) {
    try {
        return await request(`/v1/admin/organizations/${organizationId}/devices/${deviceId}/live`);
    }
    catch (err) {
        if (err instanceof HubApiError && err.status === 404)
            return null;
        throw err;
    }
}
/** Every device's latest live snapshot in the organization, in one request.
 * Devices that never sent one are absent. */
export async function listDeviceLive(organizationId) {
    return request(`/v1/admin/organizations/${organizationId}/devices/live`);
}
